import { taskKey } from "../../shared/task_status";
import { CHANNELS } from "../../shared/channels";
import { NoteEntry, ProfileStat, Reminder, RoutineStatus, StudySnapshot, TaskSnapshot } from "../../shared/types";
import { call, listen } from "../bridge";
import { byId, renderErrors, renderList } from "../dom";
import { onViewHidden, onViewShown, showView } from "../navigation";
import { flash } from "../tweens";
import { wireAskForm } from "../chat_box";
import { buildTaskItem, sortTasks } from "./task_item";
import { buildStickyNote, userNotes } from "./sticky_note";
import { buildRoutineItem, localDate } from "./routine_item";
import { canUse, myName } from "../me";
import { setupMinipiCard, showMotd } from "../minipi_card";
import { refreshBudgetQuick, setupBudgetQuick } from "../budget_quick";
import { locale, t } from "../i18n";
import { refreshFeedTile, setupFeedTile } from "../feed_tile";
import { leaveArranging, modLimit, onRackTuned, setupHomeRack } from "../home_rack";
import { openNoteSheet, setupNoteSheet } from "../note_sheet";
import { LANGUAGES } from "../../shared/languages";
import { setupVoiceModule } from "../voice_module";
import { wireDictateButton } from "../dictation";
import { setupAwardsModules } from "../awards_modules";
import { sendVisit } from "../awards_store";
import { setupChallengeModule } from "../challenge_module";
import { setupSuggestModule } from "../suggest_module";
import { setupPollModule } from "../poll_module";
import { homeNotes, loadNotePins, onNotePinsChanged, pruneNotePins } from "../note_pins";
import { mountGoalsTile } from "./goals_tile";

let lastTasks: TaskSnapshot | null = null;
let refreshGoals: () => Promise<void> = async () => undefined;

function renderTasks(snapshot: TaskSnapshot): void {
  lastTasks = snapshot;
  const plane = snapshot.tasks.filter((task) => task.source === "plane").length;
  const chix = snapshot.tasks.filter((task) => task.source === "chix").length;
  byId("today-plane-count").textContent = String(plane);
  byId("today-chix-count").textContent = String(chix);
  setNeedle("today-plane-count", plane / 15);
  setNeedle("today-chix-count", chix / 15);
  const next = sortTasks(snapshot.tasks.filter((task) => snapshot.meta?.status[taskKey(task)] !== "done")).slice(0, modLimit("tasks"));
  renderList(byId("today-tasks"), next, (task) => buildTaskItem(task, false, snapshot.meta?.status[taskKey(task)] ?? "todo"));
  byId("today-tasks-empty").hidden = next.length > 0 || snapshot.errors.length > 0;
  renderErrors(byId("today-errors"), snapshot.errors);
}

// the notes this person pinned come first, in the order they pinned them, then the newest ones fill the rest of the room
async function renderStickies(): Promise<void> {
  const [all] = await Promise.all([call<NoteEntry[]>(CHANNELS.notesList), loadNotePins()]);
  const mine = userNotes(all);
  pruneNotePins(mine.map((note) => note.name));
  const notes = homeNotes(mine, modLimit("notes"));
  renderList(byId("today-stickies"), notes, (note) => buildStickyNote(note, openNoteSheet, true));
  byId("today-stickies-empty").hidden = notes.length > 0;
}

async function renderPings(): Promise<void> {
  const pings = (await call<Reminder[]>(CHANNELS.remindersList))
    .filter((item) => !item.done)
    .sort((first, second) => first.at.localeCompare(second.at))
    .slice(0, modLimit("pings"));
  renderList(byId("today-pings"), pings, (ping) => {
    const row = document.createElement("li");
    row.className = "card ping-row";
    const when = new Date(ping.at).toLocaleString(locale(), { weekday: "short", hour: "2-digit", minute: "2-digit" });
    row.innerHTML = `<b class="ping-at"></b><span class="ping-text"></span>`;
    row.querySelector(".ping-at")!.textContent = when;
    row.querySelector(".ping-text")!.textContent = ping.text;
    row.addEventListener("click", () => showView("reminders"));
    return row;
  });
  byId("today-pings-empty").hidden = pings.length > 0;
}

async function renderJapanese(): Promise<void> {
  const snapshot = await call<StudySnapshot>(CHANNELS.japaneseToday);
  byId("today-jp-new").textContent = String(snapshot.counts.fresh);
  byId("today-jp-learn").textContent = String(snapshot.counts.learning);
  byId("today-jp-review").textContent = String(snapshot.counts.review);
  const language = LANGUAGES.find((item) => item.id === snapshot.settings.language);
  byId("today-jp-language").textContent = language ? language.native : "";
}

async function renderRoutines(): Promise<void> {
  const items = (await call<RoutineStatus[]>(CHANNELS.routinesList, localDate()))
    .filter((item) => item.due && !item.routine.paused)
    .sort((first, second) => Number(first.done) - Number(second.done));
  renderList(byId("today-routines"), items, (item) => buildRoutineItem(item, () => showView(item.routine.view || "routines")));
  byId("today-routines-empty").hidden = items.length === 0 || items.some((item) => !item.done);
  byId("today-routines").closest<HTMLElement>(".mod")!.hidden = items.length === 0;
}

async function renderStats(): Promise<void> {
  const stats = await call<ProfileStat[]>(CHANNELS.statsList);
  const latest = stats[stats.length - 1];
  // no follower counts captured yet, so the gauge shows pings still to come instead of a dead dash
  if (!latest) {
    if (!canUse("time")) return;
    const pings = (await call<Reminder[]>(CHANNELS.remindersList)).filter((item) => !item.done).length;
    byId("today-followers").textContent = String(pings);
    byId("today-followers-label").textContent = t("pings");
    setNeedle("today-followers", pings / 10);
    return;
  }
  const previous = [...stats].reverse().find((stat) => stat.handle === latest.handle && stat !== latest);
  const change = previous ? latest.followers - previous.followers : 0;
  byId("today-followers").textContent = latest.followers.toLocaleString(locale());
  setNeedle("today-followers", 0.5 + change / 40);
  byId("today-followers-label").textContent = `@${latest.handle}${change ? ` ${change > 0 ? "+" : ""}${change}` : ""}`;
}

async function saveQuickCapture(event: Event): Promise<void> {
  event.preventDefault();
  const input = byId<HTMLInputElement>("quick-capture-input");
  if (!input.value.trim()) return;
  await call(CHANNELS.notesAppendInbox, input.value);
  input.value = "";
  flash(input);
  renderStickies();
}

// swing a gauge needle, 0 is the left stop and 1 the right
export function setNeedle(numberId: string, value: number): void {
  const meter = byId(numberId).closest<HTMLElement>(".meter");
  meter?.style.setProperty("--v", String(Math.max(0, Math.min(1, value))));
}

function greeting(hour: number): string {
  const raw = myName();
  const name = (raw.charAt(0).toUpperCase() + raw.slice(1)).replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
  const vars = { name: `<em>${name}</em>` };
  if (hour < 5) return t("Late shift, {name}.", vars);
  if (hour < 12) return t("Morning, {name}.", vars);
  if (hour < 18) return t("Ready when you are, {name}.", vars);
  return t("Evening, {name}.", vars);
}

function tickClock(): void {
  const now = new Date();
  byId("today-clock").textContent = now.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit", hour12: false });
  const line = greeting(now.getHours());
  if (byId("today-greeting").innerHTML !== line) byId("today-greeting").innerHTML = line;
  byId("today-date").textContent = now.toLocaleDateString(locale(), { weekday: "long", day: "numeric", month: "long" });
}

let loaded = false;

async function loadToday(): Promise<void> {
  loaded = true;
  renderTasks(await call<TaskSnapshot>(CHANNELS.tasksList, false));
}

export async function setupTodayView(): Promise<void> {
  tickClock();
  window.setInterval(tickClock, 15_000);
  byId("today-open-tasks").addEventListener("click", () => showView("tasks"));
  document.querySelector("#view-today [data-open-chat]")?.addEventListener("click", () => showView("chat"));
  byId("quick-capture-form").addEventListener("submit", saveQuickCapture);
  byId("today-open-notes").addEventListener("click", () => showView("notes"));
  byId("today-new-note").addEventListener("click", () => openNoteSheet(null));
  byId("today-open-pings").addEventListener("click", () => showView("reminders"));
  byId("today-open-goals").addEventListener("click", () => showView("goals"));
  byId("today-open-japanese").addEventListener("click", () => showView("japanese"));
  setupNoteSheet(() => void (canUse("notes") && renderStickies()));
  setupVoiceModule(() => void (canUse("notes") && renderStickies()));
  refreshGoals = mountGoalsTile(byId("today-goals"), () => modLimit("goals"));
  byId("today-open-routines").addEventListener("click", () => showView("routines"));
  wireAskForm("today-chat-form", "today-chat-input");
  wireDictateButton(byId<HTMLButtonElement>("ask-mic"), byId<HTMLTextAreaElement>("today-chat-input"));
  wireDictateButton(byId<HTMLButtonElement>("capture-mic"), byId<HTMLInputElement>("quick-capture-input"));
  setupMinipiCard();
  setupBudgetQuick();
  setupFeedTile();
  const rack = setupHomeRack();
  setupAwardsModules();
  setupChallengeModule();
  setupSuggestModule();
  setupPollModule();
  onRackTuned(() => {
    if (lastTasks) renderTasks(lastTasks);
    if (canUse("notes")) void renderStickies();
    if (canUse("time")) void renderPings().catch(() => undefined);
    void refreshGoals();
  });
  onViewHidden("today", leaveArranging);

  listen<TaskSnapshot>(CHANNELS.tasksUpdated, renderTasks);
  listen(CHANNELS.statsUpdated, renderStats);
  listen(CHANNELS.remindersUpdated, () => {
    void renderStats().catch(() => undefined);
    if (canUse("time")) void renderPings().catch(() => undefined);
  });
  listen(CHANNELS.notesUpdated, () => void (canUse("notes") && renderStickies()));
  onNotePinsChanged(() => void (canUse("notes") && renderStickies()));
  listen(CHANNELS.routinesUpdated, () => void renderRoutines().catch(() => undefined));
  listen(CHANNELS.quickCapture, () => showView("today", "capture"));
  onViewShown("today", (detail) => {
    if (!loaded && canUse("tasks")) void loadToday();
    if (canUse("notes")) void renderStickies();
    if (canUse("routines")) void renderRoutines().catch(() => undefined);
    if (canUse("time")) void renderPings().catch(() => undefined);
    if (canUse("practice")) void renderJapanese().catch(() => undefined);
    void refreshGoals();
    void renderStats().catch(() => undefined);
    sendVisit();
    showMotd();
    refreshBudgetQuick();
    refreshFeedTile();
    if (detail === "capture") byId("quick-capture-input").focus();
  });
  await rack;
}
